import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@satyrsmc/shared/components";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventGallery } from "@/queries/event-gallery";
import { GalleryLightbox } from "./GalleryLightbox";

/**
 * The event page shows only a taste of the gallery; the full grid — and the
 * uploader attribution — lives on the gallery page behind "See all".
 */
const PREVIEW_COUNT = 3;

/** Placeholder tiles, shaped like the grid they stand in for. */
function GalleryGridSkeleton() {
  return (
    <div
      data-testid="event-gallery-skeleton"
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4"
    >
      {[0, 1, 2, 3].map((n) => (
        <Skeleton key={n} className="aspect-square w-full rounded-md" />
      ))}
    </div>
  );
}

/**
 * The approved photos from a past event. Renders nothing at all — not even the
 * heading — until the event actually has a gallery, so the vast majority of
 * events that never collect photos show no trace of the feature.
 */
export function EventGallerySection({ eventId }: { eventId: string }) {
  const { data, isLoading } = useEventGallery(eventId);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (isLoading) return <GalleryGridSkeleton />;

  const photos = data?.items ?? [];
  const galleryId = data?.gallery_id ?? null;
  if (photos.length === 0) return null;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Gallery</h2>
        {galleryId && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/gallery/${galleryId}`}>See all</Link>
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {photos.slice(0, PREVIEW_COUNT).map((photo, index) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setActiveIndex(index)}
            aria-label={`View photo ${index + 1}`}
            className="aspect-square overflow-hidden rounded-md bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
          >
            <img
              src={photo.thumbnail_url}
              alt=""
              loading="lazy"
              className="size-full object-cover transition-transform hover:scale-105"
            />
          </button>
        ))}
      </div>

      <GalleryLightbox photos={photos} index={activeIndex} onIndexChange={setActiveIndex} />
    </section>
  );
}
